interface DangerBearingFigureProps {
  size?: number;
}

/// Top-down view of a danger bearing. A prominent mark on the coast
/// (church) and a charted hazard (rocks) define a bearing limit;
/// the vessel must keep the mark bearing greater than the limit to
/// stay on the safe side.
export function DangerBearingFigure({ size = 360 }: DangerBearingFigureProps) {
  return (
    <svg
      viewBox="0 0 420 320"
      width={size}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      role="img"
      aria-label="Danger bearing diagram. A bearing from the vessel to a prominent mark divides safe from dangerous water. The vessel must keep the bearing on the safe side of the charted limit."
    >
      {/* Sea */}
      <rect x="0" y="0" width="420" height="320" fill="#dde7ef" />

      {/* Coastline along top */}
      <path
        d="M 0 0 L 420 0 L 420 80 L 380 95 L 340 105 L 300 115 L 260 125 L 220 130 L 180 130 L 140 125 L 100 115 L 60 100 L 30 85 L 0 70 Z"
        fill="#caa86b"
        stroke="#7d6a44"
        strokeWidth="0.8"
      />

      {/* Hazard: rocks marked with the chart's '+' rock symbols */}
      <g
        fill="#7d6a44"
        stroke="#3f3520"
        strokeWidth="0.8"
        fontFamily="ui-sans-serif, system-ui"
      >
        <text x="60" y="180" fontSize="14" fontWeight="700">+</text>
        <text x="75" y="190" fontSize="14" fontWeight="700">+</text>
        <text x="55" y="200" fontSize="14" fontWeight="700">+</text>
        <text x="80" y="170" fontSize="14" fontWeight="700">+</text>
      </g>
      <text
        x="50"
        y="220"
        fontSize="10"
        fill="#7d6a44"
        fontStyle="italic"
        fontFamily="ui-sans-serif, system-ui"
      >
        rocks
      </text>

      {/* Charted mark — church on the coast */}
      <g transform="translate(220 95)">
        <rect x="-3" y="-2" width="6" height="10" fill="#1c2530" />
        <polygon points="-5,-2 5,-2 0,-12" fill="#1c2530" />
      </g>
      <text
        x="232"
        y="98"
        fontSize="10"
        fontWeight="600"
        fill="#0f172a"
        fontFamily="ui-sans-serif, system-ui"
      >
        Charted mark (church)
      </text>

      {/* Danger limit line — drawn from the church through the
          outer edge of the rocks. */}
      <line
        x1="220"
        y1="95"
        x2="20"
        y2="270"
        stroke="#c0382b"
        strokeWidth="1.6"
        strokeDasharray="6 4"
      />

      {/* Vessel — on the safe side */}
      <g transform="translate(310 245) rotate(-30)">
        <polygon
          points="0,-7 5,5 -5,5"
          fill="#1c2530"
          stroke="#0f172a"
          strokeWidth="0.6"
        />
        <line x1="0" y1="-7" x2="0" y2="-16" stroke="#0f172a" strokeWidth="1.2" />
      </g>

      {/* Bearing line from vessel to mark — currently 045°, safely
          greater than the limit. */}
      <line
        x1="310"
        y1="245"
        x2="220"
        y2="95"
        stroke="#2c7a3b"
        strokeWidth="1.4"
      />
      <text
        x="252"
        y="180"
        fontSize="10"
        fill="#2c7a3b"
        fontWeight="600"
        fontFamily="ui-sans-serif, system-ui"
        transform="rotate(-60 252 180)"
      >
        Bearing now 045°
      </text>

      {/* Limit label */}
      <text
        x="115"
        y="218"
        fontSize="10"
        fill="#c0382b"
        fontWeight="600"
        fontFamily="ui-sans-serif, system-ui"
        transform="rotate(-43 115 218)"
      >
        Limit: 035° (NLT)
      </text>

      {/* Shading on the danger side */}
      <path
        d="M 220 95 L 20 270 L 0 270 L 0 95 Z"
        fill="#c0382b"
        opacity="0.10"
      />

      {/* Safe / Danger labels */}
      <text
        x="350"
        y="200"
        fontSize="12"
        fill="#2c7a3b"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui"
      >
        SAFE
      </text>
      <text
        x="40"
        y="290"
        fontSize="12"
        fill="#c0382b"
        fontWeight="700"
        fontFamily="ui-sans-serif, system-ui"
      >
        DANGER
      </text>
      <text
        x="40"
        y="304"
        fontSize="9"
        fill="#7d6a44"
        fontStyle="italic"
        fontFamily="ui-sans-serif, system-ui"
      >
        if bearing drops below 035°
      </text>
    </svg>
  );
}
